/**
 * Derivations for the agent's Deal Command Center.
 *
 * All pure, all here rather than inside the components, because these are the
 * decisions the redesign actually turns on — what counts as needing attention,
 * what "this week" means, which bucket a task belongs in — and they are worth
 * testing directly instead of through JSX.
 *
 * The brief: *"I do not want the agent's default deal page to open into a long
 * task table … Needs Attention — 3–5 actionable items only."* The cap is not
 * cosmetic. A list that can grow without bound is the task table again with
 * rounder corners, so `NEEDS_ATTENTION_LIMIT` is enforced here, after ranking,
 * and the components cannot opt out of it.
 */
import { ClientRelease, TaskStatus } from "@/enum/common.enum";
import { parseIsoDate } from "@/utils/common.utils";
import { recentCompletedItems, resolveFinancingContingencyDate } from "@/utils/deals-view.utils";
import { resolveTaskOwnership } from "@/utils/task-ownership.utils";

type AnyRecord = Record<string, any>;

/** `daysUntil` compares bare calendar days, so a resolved Date has to come back
 *  as one — in local parts, matching how the date was resolved. */
const toYmd = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export const NEEDS_ATTENTION_LIMIT = 5;
/** "This week" everywhere on this page — the groups and the milestones strip
 *  measure from this horizon. */
export const THIS_WEEK_DAYS = 7;
/** Needs attention only surfaces a task once it's this close to due. */
export const DUE_SOON_DAYS = 3;

/**
 * The default document owner for a missing category. These mirror the owners
 * of the corresponding under-contract checklist steps, so an agent using the
 * shortcut does not have to remember who should receive each file.
 */
export const MISSING_DOCUMENT_OWNER_ROLES: Record<string, string> = {
  Contract: "client",
  Disclosure: "client",
  Inspection: "agent",
  Title: "attorney",
  Loan: "lender",
  Appraisal: "lender",
};

export function missingDocumentOwnerRole(category: string): string {
  return MISSING_DOCUMENT_OWNER_ROLES[category] || "agent";
}

/** Midnight-anchored day difference. Task due dates are `YYYY-MM-DD` strings,
 *  so comparing them against a timestamp would make "due today" read as overdue
 *  for most of the day. */
export function daysUntil(dateish: string | null | undefined, now: Date): number | null {
  if (!dateish) return null;
  const raw = String(dateish).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [y, m, d] = raw.split("-").map(Number);
  const target = Date.UTC(y, m - 1, d);
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((target - today) / 86_400_000);
}

/** Days since `listed_at`, frozen on the day it went under contract. Null when
 *  the list date was never recorded (listings that predate the stamp). */
export function daysOnMarket(
  deal: { listed_at?: string | null; under_contract_at?: string | null },
  now: Date,
): number | null {
  const end = deal.under_contract_at ? new Date(deal.under_contract_at) : now;
  const days = daysUntil(deal.listed_at, end);
  return days === null ? null : Math.max(0, -days);
}

/** Match the backend lifecycle predicate. Completed, waived, and inactive
 * tasks are closed work and must never be promoted as overdue or due soon. */
const isOpen = (task: AnyRecord) =>
  task.status !== TaskStatus.COMPLETE
  && !task.waived_at
  && task.is_active !== false;

/** Work the host agency owns. Anything else is somebody else's move, which is
 *  what separates "I am behind" from "I am waiting". */
const isMine = (task: AnyRecord) => !task.assigned_role || task.assigned_role === "agent";

/**
 * The things an agent should act on, worst first: anything already overdue,
 * then their own open tasks due within the next few days. Waiting-on-others and
 * missing-document rows were pulled — the panel is now just "what's on me and
 * about to be late."
 */
export function buildNeedsAttention(
  { deal, tasks = [], now = new Date() }:
  { deal: AnyRecord; tasks?: AnyRecord[]; now?: Date },
): AnyRecord[] {
  const items: AnyRecord[] = [];

  tasks.filter(isOpen).forEach((task) => {
    const days = daysUntil(task.due_date, now);
    if (days !== null && days < 0) {
      items.push({
        key: `overdue-${task.id}`, rank: 0, kind: "overdue", task,
        owner: resolveTaskOwnership(task, deal),
        title: task.title,
        detail: `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"}`,
        tone: "rust", days,
      });
    }
  });

  tasks.filter(isOpen).forEach((task) => {
    const days = daysUntil(task.due_date, now);
    if (days === null || days < 0 || days > DUE_SOON_DAYS) return;
    items.push({
      key: `soon-${task.id}`, rank: 3, kind: "due_soon", task,
      owner: resolveTaskOwnership(task, deal),
      title: task.title,
      detail: days === 0 ? "Due today" : `${days} day${days === 1 ? "" : "s"} left`,
      tone: "ink", days,
    });
  });

  return items
    .sort((a, b) => a.rank - b.rank
      || (a.days ?? 9999) - (b.days ?? 9999)
      || String(a.title).localeCompare(String(b.title)))
    .slice(0, NEEDS_ATTENTION_LIMIT);
}

/**
 * Meaningful events inside the next seven days — dated tasks *and* the contract
 * milestones, interleaved, because an agent thinks about the week as one line
 * rather than as two lists.
 */
export function buildThisWeek(
  { deal, tasks = [], now = new Date() }: { deal: AnyRecord; tasks?: AnyRecord[]; now?: Date },
): AnyRecord[] {
  const terms = deal.contract_terms || {};
  const events: AnyRecord[] = [];

  const milestone = (date: string | undefined, key: string, label: string) => {
    const days = daysUntil(date, now);
    if (days === null || days < 0 || days > THIS_WEEK_DAYS) return;
    events.push({ key, kind: "milestone", title: label, days, date });
  };
  milestone(terms.due_diligence_end_date, "ms-dd", "Due diligence ends");
  // Resolved, not read: the contingency may be stored as a day count from the
  // contract date rather than a date, and reading only the date field dropped
  // those deals off this strip entirely. Cash deals resolve to null, because
  // surfacing a financing deadline on one would be inventing a deadline.
  const finDate = resolveFinancingContingencyDate(deal);
  if (finDate) milestone(toYmd(finDate), "ms-fin", "Financing contingency expires");
  milestone(terms.closing_date, "ms-close", "Closing");

  tasks.filter(isOpen).forEach((task) => {
    const days = daysUntil(task.due_date, now);
    if (days === null || days < 0 || days > THIS_WEEK_DAYS) return;
    events.push({
      key: `task-${task.id}`, kind: "task", title: task.title, days,
      date: task.due_date, task,
    });
  });

  return events.sort((a, b) => a.days - b.days || String(a.title).localeCompare(String(b.title)));
}

/**
 * The four collapsed drawers that replace the raw table.
 *
 * Buckets are mutually exclusive and tested for it — a task showing up twice
 * would make the counts lie, and the counts are the only thing visible while a
 * drawer is shut. Precedence: done, then not-ours, then by date.
 */
export function buildTaskGroups(
  { tasks = [], now = new Date() }: { tasks?: AnyRecord[]; now?: Date },
): AnyRecord[] {
  const thisWeek: AnyRecord[] = [];
  const upcoming: AnyRecord[] = [];
  const waiting: AnyRecord[] = [];
  const completed: AnyRecord[] = [];

  tasks.forEach((task) => {
    // A client milestone the agent has confirmed happened is finished work for
    // the agent, even though confirming deliberately does not tick it complete.
    const confirmed = task.client_release === ClientRelease.AFTER_CONFIRMED && task.confirmed_at;
    if (!isOpen(task) || confirmed) { completed.push(task); return; }
    if (!isMine(task)) { waiting.push(task); return; }
    const days = daysUntil(task.due_date, now);
    // Undated work is Upcoming, never This Week: an agent should not be told
    // something is due in the next seven days when nobody has said when it is
    // due at all.
    if (days !== null && days <= THIS_WEEK_DAYS) thisWeek.push(task);
    else upcoming.push(task);
  });

  return [
    { key: "this_week", label: "This week", tasks: thisWeek.sort(byChecklist), defaultOpen: true },
    { key: "upcoming", label: "Upcoming", tasks: upcoming.sort(byChecklist), defaultOpen: false },
    { key: "waiting", label: "Waiting on others", tasks: waiting.sort(byChecklist), defaultOpen: false },
    { key: "completed", label: "Completed", tasks: completed.sort(byChecklist), defaultOpen: false },
  ];
}

/** The checklist's own order — the template is chronological, and it is what
 *  the agent's drag sets. Sorting by due date put the two dated closing rows
 *  above everything undated and undid every drag. */
const byChecklist = (a: AnyRecord, b: AnyRecord) => (a.sort_order ?? 0) - (b.sort_order ?? 0);

/**
 * Checklist progress for the Deal Status banner: next open task, last completed
 * task, done/total. The status itself is the server's `snapshot.health` — this
 * never decides whether a deal is on track.
 * Waived and inactive rows are out of scope, as they are everywhere else.
 */
export function buildDealStatus(
  { tasks = [], now = new Date() }: { tasks?: AnyRecord[]; now?: Date },
): AnyRecord {
  const live = tasks.filter((task) => !task.waived_at && task.is_active !== false);
  const open = live.filter(isOpen);
  const done = live.filter((task) => task.status === TaskStatus.COMPLETE);
  const late = (task: AnyRecord) => (daysUntil(task.due_date, now) ?? 0) < 0;
  return {
    // Checklist order, not due date: on a new deal almost nothing is dated, so
    // "earliest due" picked the closing-figures row — the last step — as next.
    next: open.filter((task) => !late(task)).sort(byChecklist)[0] ?? null,
    // Furthest step done in checklist order, the same order the client journey
    // uses. Click time made "Completed" lag behind a later step ticked earlier,
    // or one ticked a moment ago that has no `completed_at` yet.
    lastCompleted: recentCompletedItems(done, 1)[0] ?? null,
    doneCount: done.length,
    total: live.length,
  };
}

/** Where the deal is and what comes next, for the linear step tracker. */
export function buildStagePipeline(
  { deal, phases, labels }: { deal: AnyRecord; phases: readonly string[]; labels: AnyRecord },
): AnyRecord {
  const currentIndex = phases.indexOf(deal.phase);
  return {
    steps: phases.map((phase, index) => ({
      phase,
      label: labels[phase] || phase,
      state: index < currentIndex ? "done" : index === currentIndex ? "current" : "future",
    })),
    currentIndex,
    nextPhase: currentIndex >= 0 ? phases[currentIndex + 1] ?? null : null,
  };
}

/** Wording for a whole-day distance still ahead. Shared by every stat card so
 *  the countdown phrasing cannot drift. Past deadlines never count "days past"
 *  (client item 6) — see `buildDealStatStrip` for what they say instead. */
function daysPhrase(days: number): string {
  if (days === 0) return "Today";
  const unit = days === 1 ? "day" : "days";
  return `${days} ${unit} left`;
}

const shortDate = (date: Date) => date.toLocaleDateString(undefined, { month: "short", day: "numeric" });

/** Urgency band, matching the hero cards' thresholds so the compact strip and
 *  the detailed cards never disagree about what counts as urgent. */
function urgencyFor(days: number): string {
  if (days < 0) return "overdue";
  if (days <= 3) return "urgent";
  if (days <= 7) return "warning";
  return "calm";
}

/**
 * The four compact cards across the top of the deal page.
 *
 * The mockup's strip, built from data the page already had: three contract
 * deadlines and a live count of what needs the agent. Deliberately a pure
 * builder rather than logic inside the cards, because "which cards exist" is a
 * real decision — a cash deal has no financing contingency, and a deal that is
 * not yet under contract has none of the three — and it is worth testing
 * directly.
 *
 * `target` travels with each card so the component can render the live
 * Days/Hrs/Mins/Secs ticker beneath the summary line. The ticker was an
 * explicit client decision and the compact layout keeps it rather than
 * trading it away for the mockup's single line.
 */
export function buildDealStatStrip(
  { deal, attentionCount = 0, now = new Date() }:
  { deal: AnyRecord; attentionCount?: number; now?: Date },
): AnyRecord[] {
  const cards: AnyRecord[] = [];
  const terms = deal?.contract_terms || {};
  const underContract = deal?.phase === "under_contract";

  const dated = (dateish: string | null | undefined, key: string, label: string) => {
    if (!underContract) return;
    const days = daysUntil(dateish, now);
    if (days === null) return;
    const target = parseIsoDate(dateish, /* endOfDay */ true);
    // A contingency window that has run out simply ended — that is the normal
    // course of a deal, not a failure, so it reads "Ended Sep 17" in a calm
    // tone with no clock counting up behind it.
    if (days < 0) {
      cards.push({
        key, label, days, urgency: "calm",
        value: `Ended ${shortDate(target!)}`,
        target: null,
        testid: `deal-stat-${key}`,
      });
      return;
    }
    cards.push({
      key, label, days, urgency: urgencyFor(days),
      value: daysPhrase(days),
      target,
      testid: `deal-stat-${key}`,
    });
  };

  if (underContract && deal.under_contract_at) {
    const target = parseIsoDate(deal.under_contract_at);
    if (target) {
      const days = daysUntil(deal.under_contract_at, now);
      cards.push({
        key: "under_contract", label: "Under contract", days,
        urgency: "calm", value: target.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        target, testid: "deal-stat-under-contract",
      });
    }
  }
  dated(terms.due_diligence_end_date, "dd", "Due diligence");
  // Resolved rather than read: the contingency may be stored as a day count
  // from the contract date, and a cash deal resolves to null because it has no
  // loan to be contingent on.
  const finDate = resolveFinancingContingencyDate(deal);
  if (finDate) dated(toYmd(finDate), "financing", "Financing");

  if (underContract) {
    const days = daysUntil(terms.closing_date, now);
    if (days !== null) {
      const target = parseIsoDate(terms.closing_date, /* endOfDay */ true);
      const overdue = days < 0;
      const n = Math.abs(days);
      cards.push({
        key: "closing", label: "Closing", days, urgency: urgencyFor(days),
        // The mockup prints the date and the distance together, because a
        // closing is the one deadline an agent quotes out loud by date. A
        // closing date behind a deal still under contract genuinely is late.
        value: overdue
          ? `${shortDate(target!)}, overdue by ${n} ${n === 1 ? "day" : "days"}`
          : `${shortDate(target!)}, ${daysPhrase(days)}`,
        target: overdue ? null : target,
        testid: "deal-stat-closing",
      });
    }
  }

  return cards;
}
